/* import __COLOCATED_TEMPLATE__ from './seller-profile-action-buttons.hbs'; */
import $ from 'jquery';
import { scheduleOnce } from '@ember/runloop';
import { A } from '@ember/array';
import EmberObject, { action, computed } from '@ember/object';
import { not, readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { BUTTON_TYPES } from "../../constants/lsg";
import { showModal } from "sh-www/utils/modal-controls";
import { APPLICATION } from "../../models/application";
import {
  BLOCK_FREELANCER_ELEMENT_ID, PROFILE_SUB_SECTION_STATES,
  PROFILE_OVERLAY_SCROLL_VIEW_CLASS,
} from "../../constants/components/profile";
import { EMBER_ROUTE_NAMES } from "../../constants/routes";
import { absoluteStoryhunterUrl } from "../../utils/url";

export default Component.extend({
  /**
   * HTML Component properties
   */
  tagName: 'div',
  classNames: ['SH-c-seller-profile-action-buttons'],
  classNameBindings: ['unauthenticated:SH-c-seller-profile-action-buttons--unauthenticated'],

  /**
   * Register services
   */
  applicationCommandService : service('commands/application-commands'),
  applicationState: service('states/application'),
  applicationStateManager: service('state-managers/application'),
  errorTracker: service('error-tracker'),
  router: service(),
  companyConnectionCommands: service('commands.company-connection-commands'),
  userCommands: service('commands.user-commands'),
  session: service(),

  currentUser: readOnly('session.currentUser'),

  /** @param {User} user */
  user: null,

  /** @param {Boolean} isViewingSelf */
  isViewingSelf: false,

  /** @param {Boolean} showSaveOption */
  showSaveOption: true,

  unauthenticated: not('session.isAuthenticated'),

  closeOverlay: () => {},

  /** @param {Function} updateProfileSubSection */
  updateProfileSubSection: () => {},

  handleSellerAddProjectDigest: null,

  /**
   * @type {Boolean}    True if freelancer is blocked
   */
  _isFreelancerBlocked: readOnly('user.isBlocked'),

  /**
   * @type {Boolean}    True if we want to disable the hire button.
   */
  _isHireButtonDisabled: false,

  /**
   * @type {Boolean}    True when we're making an API call to save/unsave connection status.
   */
  _isSavingConnectionStatus: false,

  /**
   * @type {Boolean}    True when we want to show the save button
   */
  _isShowSaveButton: computed('user.inCurrentUsersNetwork', 'showSaveOption', function() {
    return !this.user.inCurrentUsersNetwork && this.showSaveOption;
  }),

  /**
   * The array of menu dropdown options in the format:
   *
   * [{
   *    title: 'Edit',
   *    onClick: Function   // callback function for the click on this menu item
   *    hasSeparatorAfter: hasSeparatorAfter
   *  },{...}]
   *
   * @type {Ember.MutableArray}
   */
  _menuDropdownOptions: computed('user.inCurrentUsersNetwork', 'showSaveOption', function() {
    let menuOptions     = A();

    if (this.get('currentUser.isAdmin')) {
      menuOptions.pushObject({
        title: 'Admin - View In Search',
        onClick: () => {window.location.assign(absoluteStoryhunterUrl('/admin/users?idSlugEmail=' + this.get('user.id')));},
      });

      menuOptions.pushObject({
        title: 'Manage Billing',
        onClick: () => {
          this.router.transitionTo(EMBER_ROUTE_NAMES.ADMIN__USER__BILLING, this.user.id);
        },
      });

      return menuOptions;
    }

    menuOptions.pushObject({
      title: 'Invite to posting',
      onClick: () => this._onInviteToPostingClicked(),
      hasSeparatorAfter: true
    });

    if (this.get('user.inCurrentUsersNetwork') && this.showSaveOption) {
      menuOptions.pushObject({
        title: 'Remove from your saved list',
        onClick: () => this._onUnSaveClicked(),
      });
    }

    menuOptions.pushObject({
      title: 'Block',
      onClick: () => this._onBlockClicked(),
    });

    return menuOptions;
  }),

  /**
   * The button type of the secondary buttons in the action area.
   *
   * @type {String}   of type BUTTON_TYPES.*
   */
  _secondaryButtonType: computed(function() {
    return BUTTON_TYPES.SECONDARY_REGULAR_FULL_WIDTH;
  }),

  _onInviteToPostingClicked: action(function() {
    const modalModel = {
      onInviteSucceeded: () => {
        this.applicationStateManager.addToast(`You successfully invited ${this.user.firstName} to your posting.`);
      },
      userToInvite: this.user
    };

    showModal(this, 'lsg/lsg-invite-to-posting-modal', modalModel);
  }),

  _onUnSaveClicked: action(function() {
    const { user } = this;

    showModal(this, 'confirmation', {
      titleText: 'Remove freelancer?',
      bodyText: `Are you sure you want to remove ${user.fullName} from your 'Saved' list?`,
      actionButtonText: 'Remove',
      cancelButtonText: 'Cancel',
      isShowCancelButton: true,
      confirmAction: () => {
        this.set('_isSavingConnectionStatus', true);

        (async () => {
          try {
            await this.companyConnectionCommands.updateNetworkConnection(
              this.session.currentUser.company,
              user,
              null,
              false,
            );
            await this.userCommands.getUser(user.id);
          } catch {
            this.applicationStateManager.addToast('Error removing user from company network');
          } finally {
            if (!this.isDestroying) {
              this.set('_isSavingConnectionStatus', false);
            }
          }
        })();
      },
    });
  }),

  _onBlockClicked: action(function() {
    const modalModel = {
      freelancerProfile: this.user
    };

    showModal(this, 'block-freelancer-modal', modalModel);
  }),

  actions: {
    /** Called when the 'Add Project' button is clicked. */
    _onAddProjectClicked() {
      const model = {
        addProjectDigestCallback: (projectDigest) => this.handleSellerAddProjectDigest.perform(projectDigest),
      };

      showModal(this, 'work-samples/project', model);
    },

    /**
     * Called when the user clicks on the 'hire' button
     */
    _onHireClicked() {
      this.hireFreelancer();
    },

    /**
     * Called when the user clicks on the 'message' button
     */
    _onMessageClicked: function() {
      if (this.get('currentUser.company.isNeedsCreditCard')) {
        return showModal(this, 'payments/stripe-cc-modal', {
          seller: this.get('user'),
        });
      }

      showModal(this, 'lsg/lsg-send-message', {
        userToSendMessageTo: this.get('user'),
      });
    },

    /**
     * Called when the user clicks on the 'save' button
     */
    async _onSaveClicked() {
      const { user } = this;

      this.set('_isSavingConnectionStatus', true);

      try {
        await this.companyConnectionCommands.updateNetworkConnection(
          this.session.currentUser.company,
          user,
          null,
          true
        );
        await this.userCommands.getUser(user.id);
      } catch {
        this.applicationStateManager.addToast('Error adding user to company network');
      } finally {
        if (!this.isDestroying) {
          this.set('_isSavingConnectionStatus', false);
        }
      }
    },

    /**
     * Called when user clicks view details from the block user overlay.
     *
     * @param {MouseEvent} event
     */
    _onViewBlockUserDetailsClick(event) {
      event.stopPropagation();
      event.preventDefault();

      // Navigate to Marketplace profile if not there already
      if (this.router.currentURL !== (`/${this.user.slug}`)) {
        const transition = this.router.transitionTo(EMBER_ROUTE_NAMES.PROFILE, this.user.slug);
        transition.data = { blockedDetails: true };
      }

      else {
        this.get('updateProfileSubSection')(PROFILE_SUB_SECTION_STATES.SUB_SECTION_YOUR_INFO)
        .then(() => {
          scheduleOnce('afterRender', this, () => {
            const blockFreelancerEl = $(`#${BLOCK_FREELANCER_ELEMENT_ID}`);
            const headerOffset = 65;
            const scrollTop = blockFreelancerEl.offset().top - headerOffset;
            const profileOverlayScrollViewEl = $(`.${PROFILE_OVERLAY_SCROLL_VIEW_CLASS}`);
            const scrollEl = profileOverlayScrollViewEl.length
              ? profileOverlayScrollViewEl
              : $([document.documentElement, document.body]);

            scrollEl.animate({ scrollTop }, 500);
          });
        });
      }
    },
  },

  /**
   * @private
   */
  hireFreelancer() {
    if (this.get('currentUser.company.isNeedsCreditCard')) {
      return showModal(this, 'payments/stripe-cc-modal', {
        seller: this.get('user'),
        onSuccess: () => this.hireFreelancer(),
      });
    }

    this.set('_isHireButtonDisabled', true);

    this.get('applicationCommandService').loadApplications(
      undefined,
      APPLICATION.SIMPLE_STATUS.IN_PROGRESS,
      undefined,
      false,
      undefined,
      1,
      20,
      undefined,
      '',
      this.get('user.id'),
      this.get('currentUser.company.id')
    ).then(dashboardApplicationList => {
      let freelancer = this.get('user'),
        projects = dashboardApplicationList.get('applications');

      if (projects.get('length')) {
        const modalData = EmberObject.create({
          freelancer: freelancer,
          freelancerProjects: projects
        });

        showModal(this, 'projects/existing-projects-modal', modalData);
      } else {
        this.applicationStateManager.setNextRoute(EMBER_ROUTE_NAMES.PROJECT__CREATE__DIRECT, freelancer.slug);
        this.closeOverlay?.();
      }
    }).catch(e => {
      this.get('errorTracker').error(
        '_onHireClicked -- could not load project for freelancer id ' + this.get('user.id'),
        this,
        e);
    }).finally(() => this.set('_isHireButtonDisabled', false));
  },
});
